"use client";

import {Alert, Box, Flex, Field, Icon, Input, Link, Stack, Text, Textarea} from "@chakra-ui/react";
import {AtSymbolIcon, BuildingOfficeIcon, EnvelopeIcon} from "@heroicons/react/24/outline";
import {useCallback, useState} from "react";
import Button from "../../components/Button";
import {Container} from "../../components/Container";
import Heading from "../../components/Heading";
import useErrorableState from "../utils/useErrorableState";
import {API_HOST} from "../utils/apiHost";

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [_success, setSuccess] = useState(false);
  const [name, setName, nameError, setNameError] = useErrorableState<string>(undefined);
  const [email, setEmail, emailError, setEmailError] = useErrorableState<string>(undefined);
  const [phone, setPhone, phoneError, setPhoneError] = useErrorableState<string>(undefined);
  const [message, setMessage, messageError, setMessageError] = useErrorableState<string>(undefined);

  const success = _success && name === undefined && email === undefined && phone === undefined && message === undefined;

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSuccess(false);
      setLoading(true);

      try {
        const resp = await fetch(`${API_HOST}/support_emails/contact_us`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name,
            email,
            phone,
            message,
          }),
        });

        if (resp.ok) {
          setSuccess(true);
          setName(undefined);
          setEmail(undefined);
          setPhone(undefined);
          setMessage(undefined);
          setNameError(undefined);
          setEmailError(undefined);
          setPhoneError(undefined);
          setMessageError(undefined);
        }

        const json = await resp.json();
        if (json.name) {
          setNameError(json.name[0].message);
        }

        if (json.email) {
          setEmailError(json.email[0].message);
        }

        if (json.phone) {
          setPhoneError(json.phone[0].message);
        }

        if (json.message) {
          setMessageError(json.message[0].message);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    },
    [
      name,
      setName,
      setNameError,
      email,
      setEmail,
      setEmailError,
      phone,
      setPhone,
      setPhoneError,
      message,
      setMessage,
      setMessageError,
    ],
  );

  return (
    <Container pb={[28, 32]} pt={[20, 32]}>
      <Heading mb={8}>Get in touch</Heading>
      <Flex maxW="full" flexWrap="wrap" gap={20}>
        <Stack flex={1} minW={["full", "full", 1]} gap={10}>
          <Text fontSize="lg" color="gray.600">
            Learn how we can help you breeze through procurement and accelerate sales cycles through AI-driven
            automation.
          </Text>
          <Stack color="gray.700" gap={6} fontSize="sm">
            <Flex>
              <Icon mr={4} color="blue.500" size="2xl">
                <BuildingOfficeIcon />
              </Icon>
              <Stack gap={1} mt="-2px">
                <Text color="blue.500" textTransform="uppercase" fontWeight="bold" fontSize="xs">
                  Operating Address
                </Text>
                <Text>
                  Spaces Victoria
                  <br />
                  25 Wilton Rd
                  <br />
                  London SW1V 1LW
                  <br />
                  UK
                </Text>
              </Stack>
            </Flex>
            <Flex>
              <Icon mr={4} color="blue.500" size="2xl">
                <EnvelopeIcon />
              </Icon>
              <Stack gap={1} mt="-2px">
                <Text color="blue.500" textTransform="uppercase" fontWeight="bold" fontSize="xs">
                  Registered Address
                </Text>
                <Text>
                  71-75 Shelton Street
                  <br />
                  London WC2H 9JQ
                  <br />
                  UK
                </Text>
              </Stack>
            </Flex>
            <Flex>
              <Icon mr={4} color="blue.500" size="2xl">
                <AtSymbolIcon />
              </Icon>
              <Stack gap={1} mt="-2px">
                <Text color="blue.500" textTransform="uppercase" fontWeight="bold" fontSize="xs">
                  Enquiries
                </Text>
                <Link href="mailto:hello@platformed.com" color="inherit">
                  hello@platformed.com
                </Link>
              </Stack>
            </Flex>
          </Stack>
        </Stack>
        <Box flex={1} minW={["full", "full", 1]}>
          <form onSubmit={onSubmit} noValidate>
            <Stack gap={8}>
              <Field.Root required invalid={nameError !== undefined}>
                <Field.Label>
                  Name
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input value={name || ""} onChange={e => setName(e.target.value)} />
                <Field.ErrorText>{nameError}</Field.ErrorText>
              </Field.Root>
              <Field.Root required invalid={emailError !== undefined}>
                <Field.Label>
                  Email
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input
                  placeholder="you@domain.com"
                  value={email || ""}
                  onChange={e => setEmail(e.target.value)}
                  type="email"
                />
                <Field.ErrorText>{emailError}</Field.ErrorText>
              </Field.Root>
              <Field.Root invalid={phoneError !== undefined}>
                <Field.Label>
                  Phone
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input value={phone || ""} onChange={e => setPhone(e.target.value)} />
                <Field.ErrorText>{phoneError}</Field.ErrorText>
              </Field.Root>
              <Field.Root required invalid={messageError !== undefined}>
                <Field.Label>
                  Message {messageError}
                  <Field.RequiredIndicator />
                </Field.Label>
                <Textarea value={message || ""} onChange={e => setMessage(e.target.value)} />
                <Field.ErrorText>{messageError}</Field.ErrorText>
              </Field.Root>
              {success ? (
                <Alert.Root status="success">
                  <Alert.Indicator />
                  Message sent. We&apos;ll be in touch soon!
                </Alert.Root>
              ) : (
                <Flex justify="flex-end">
                  <Button colorPalette="green" type="submit" loading={loading} disabled={success}>
                    Submit
                  </Button>
                </Flex>
              )}
            </Stack>
          </form>
        </Box>
      </Flex>
    </Container>
  );
};

export default ContactUs;
